import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoCloudUploadOutline, IoCheckmarkCircleOutline, IoAlertCircleOutline, IoEyeOutline, IoTrashOutline, IoTimeOutline, IoPlayOutline, IoPlayCircleOutline } from 'react-icons/io5';
import { FaFilePdf } from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';
import { IoDocumentAttachOutline } from "react-icons/io5";
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function Documents() {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState({
        idFront: null,
        idBack: null,
        addressProof: null
    });

    // Store file references directly to avoid React state serialization issues
    const [fileRefs, setFileRefs] = useState({
        idFront: null,
        idBack: null,
        addressProof: null
    });

    const [uploadStatus, setUploadStatus] = useState({
        idFront: 'pending',
        idBack: 'pending',
        addressProof: 'pending'
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingSteps, setUploadingSteps] = useState({
        idFront: false,
        idBack: false,
        addressProof: false
    });
    const [filePreviews, setFilePreviews] = useState({
        idFront: null,
        idBack: null,
        addressProof: null
    });

    const [fileInfo, setFileInfo] = useState({
        idFront: null,
        idBack: null,
        addressProof: null
    });
    const [activeTab, setActiveTab] = useState('idFront');
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);

    // Step definitions
    const steps = [
        {
            id: 1,
            title: t('documents.documentSteps.idFront.title'),
            description: t('documents.documentSteps.idFront.description'),
            documentType: 'idFront',
            icon: <IoDocumentTextOutline size={24} />
        },
        {
            id: 2,
            title: t('documents.documentSteps.idBack.title'),
            description: t('documents.documentSteps.idBack.description'),
            documentType: 'idBack',
            icon: <IoDocumentTextOutline size={24} />
        },
        {
            id: 3,
            title: t('documents.documentSteps.addressProof.title'),
            description: t('documents.documentSteps.addressProof.description'),
            documentType: 'addressProof',
            icon: <IoDocumentTextOutline size={24} />
        }
    ];

    // Check existing documents on page load
    useEffect(() => {
        const checkExistingDocuments = async () => {
            try {
                setIsLoadingDocuments(true);
                const response = await api.getExistingDocuments();

                if (response && response.data) {
                    const existingDocuments = response.data;
                    const completedStepsList = [];
                    let nextStep = 1;

                    // Map document types to steps
                    const documentMapping = {
                        'identity': { stepId: 1, documentType: 'idFront' },
                        'bank': { stepId: 2, documentType: 'idBack' },
                        'addressproof': { stepId: 3, documentType: 'addressProof' }
                    };

                    // Check which documents exist
                    existingDocuments.forEach(doc => {
                        const mapping = documentMapping[doc.fileName];
                        if (mapping) {
                            completedStepsList.push(mapping.stepId);
                            setUploadStatus(prev => ({
                                ...prev,
                                [mapping.documentType]: 'uploaded'
                            }));
                        }
                    });

                    setCompletedSteps(completedStepsList);

                    // Debug log
                    console.log('Document check debug:', {
                        completedStepsList,
                        length: completedStepsList.length,
                        stepsLength: steps.length,
                        existingDocuments
                    });

                    // Determine next step
                    if (completedStepsList.length === 0) {
                        // No documents uploaded, start from step 1 (Kimlik Ön Yüzü)
                        nextStep = 1;
                        console.log('No documents, nextStep = 1');
                    } else if (completedStepsList.length < 3) {
                        // Some documents uploaded, start from next unuploaded step
                        nextStep = completedStepsList.length + 1;
                        console.log('Some documents uploaded, nextStep =', nextStep);
                    } else {
                        // All 3 documents uploaded, move to final step (step 4)
                        nextStep = 4;
                        console.log('All documents uploaded, nextStep = 4');
                    }

                    console.log('Final nextStep:', nextStep);
                    setCurrentStep(nextStep);
                }
            } catch (error) {
                console.error('Error checking existing documents:', error);
                // Continue with default state if API fails
            } finally {
                setIsLoadingDocuments(false);
            }
        };

        checkExistingDocuments();
    }, []);

    const handleFileChange = (documentType, file) => {
        if (file) {
            setDocuments(prev => ({
                ...prev,
                [documentType]: file
            }));

            // Store file reference separately to avoid React state issues
            setFileRefs(prev => ({
                ...prev,
                [documentType]: file
            }));

            // Store file info
            setFileInfo(prev => ({
                ...prev,
                [documentType]: {
                    name: file.name,
                    size: file.size,
                    type: file.type
                }
            }));

            // Create preview URL for images
            if (file.type.startsWith('image/')) {
                const previewUrl = URL.createObjectURL(file);
                setFilePreviews(prev => ({
                    ...prev,
                    [documentType]: previewUrl
                }));
            }
        }
    };

    const handleIndividualUpload = async (documentType) => {
        const file = fileRefs[documentType];

        if (!file) {
            toast.error(t('documents.upload.selectFileFirst'));
            return;
        }

        setUploadingSteps(prev => ({
            ...prev,
            [documentType]: true
        }));

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.uploadDocument(formData, documentType);

            // Check for errors in response
            if (response.errors && response.errors.length > 0) {
                const errorMessage = response.errors[0].message || 'Upload failed';
                throw new Error(errorMessage);
            }

            // If no errors, mark as successful
            setUploadStatus(prev => ({
                ...prev,
                [documentType]: 'uploaded'
            }));

            const currentStepData = steps.find(step => step.documentType === documentType);
            if (currentStepData) {
                setCompletedSteps(prev => {
                    const nextCompleted = [...prev, currentStepData.id];

                    // If all three docs uploaded, go to final step (4)
                    if (nextCompleted.length === steps.length) {
                        setTimeout(() => {
                            setCurrentStep(4);
                        }, 300);
                        return nextCompleted;
                    }

                    // Otherwise, advance to next missing document step
                    for (let i = 1; i <= steps.length; i++) {
                        if (!nextCompleted.includes(i)) {
                            setTimeout(() => {
                                setCurrentStep(i);
                            }, 300);
                            break;
                        }
                    }

                    return nextCompleted;
                });
            }

            toast.success(`${currentStepData?.title} ${t('documents.upload.uploadSuccess')}`);
        } catch (error) {
            console.error('Document upload error:', error);
            toast.error(t('documents.upload.uploadFailed'));
        } finally {
            setUploadingSteps(prev => ({
                ...prev,
                [documentType]: false
            }));
        }
    };

    const handleFileRemove = (documentType, e) => {
        e.preventDefault();
        e.stopPropagation();

        // Clean up preview URL
        if (filePreviews[documentType]) {
            URL.revokeObjectURL(filePreviews[documentType]);
        }

        setDocuments(prev => ({
            ...prev,
            [documentType]: null
        }));
        setFileRefs(prev => ({
            ...prev,
            [documentType]: null
        }));
        setUploadStatus(prev => ({
            ...prev,
            [documentType]: 'pending'
        }));
        setFilePreviews(prev => ({
            ...prev,
            [documentType]: null
        }));
        setFileInfo(prev => ({
            ...prev,
            [documentType]: null
        }));

        // Remove from completed steps
        const stepData = steps.find(step => step.documentType === documentType);
        if (stepData) {
            setCompletedSteps(prev => prev.filter(stepId => stepId !== stepData.id));
        }

        toast.success(t('documents.upload.removeFile'));
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const documentTabs = [
        {
            key: 'idFront',
            title: 'Kimlik Ön Yüzü',
            description: 'TC Kimlik kartınızın ön yüzünü yükleyin',
            required: true,
            acceptedTypes: 'image/*',
            maxSize: '5MB'
        },
        {
            key: 'idBack',
            title: 'Kimlik Arka Yüzü',
            description: 'TC Kimlik kartınızın arka yüzünü yükleyin',
            required: true,
            acceptedTypes: 'image/*',
            maxSize: '5MB'
        },
        {
            key: 'addressProof',
            title: 'İkametgah Belgesi',
            description: 'Son 3 ay içinde alınmış ikametgah belgenizi yükleyin',
            required: true,
            acceptedTypes: 'image/*,application/pdf',
            maxSize: '10MB'
        }
    ];


    const handleSubmit = async (e) => {
        e.preventDefault();

        const requiredDocs = documentTabs.filter(doc => doc.required);
        const missingDocs = requiredDocs.filter(doc => !documents[doc.key]);

        if (missingDocs.length > 0) {
            toast.error(t('documents.upload.uploadAllRequired'));
            return;
        }

        setIsSubmitting(true);

        try {
            // API çağrısı burada yapılacak
            await new Promise(resolve => setTimeout(resolve, 3000)); // Simülasyon

            toast.success(t('documents.upload.submitSuccess'));

            // Formu sıfırla
            setDocuments({
                idFront: null,
                idBack: null,
                addressProof: null
            });

            setUploadStatus({
                idFront: 'pending',
                idBack: 'pending',
                addressProof: 'pending'
            });
        } catch (error) {
            console.error('Belge yükleme hatası:', error);
            toast.error(t('documents.upload.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'uploaded':
                return <IoCheckmarkCircleOutline className="text-success" size={20} />;
            case 'rejected':
                return <IoAlertCircleOutline className="text-danger" size={20} />;
            case 'approved':
                return <IoCheckmarkCircleOutline className="text-success" size={20} />;
            default:
                return <IoDocumentTextOutline className="text-muted" size={20} />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'uploaded':
                return t('documents.status.uploaded');
            case 'rejected':
                return t('documents.status.rejected');
            case 'approved':
                return t('documents.status.approved');
            default:
                return t('documents.status.pending');
        }
    };

    // Show loading state while checking existing documents
    if (isLoadingDocuments) {
        return (
            <div className='documents-layout'>
                <div className="container py-lg-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="deposit-page">
                                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                                    <div className="text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('documents.loading.loading')}</span>
                                        </div>
                                        <p className="mt-3">{t('documents.loading.checkingDocuments')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='documents-layout'>
            <div className="container py-lg-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="deposit-page">
                            {/* Header */}
                            <div className="deposit-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-3">
                                        <h1 className="deposit-title">{t('documents.title')}</h1>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4">
                                {/* Sol Kolon - Para Çekme İşlemleri */}
                                <div className="container">
                                    {/* Veri Güvenliği Bilgi Kutusu */}
                                    <div className="info-section mb-4">
                                        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.75rem' }}>{t('documents.dataSecurity')}</h3>
                                        <p className='mb-0' style={{ fontSize: 15, fontWeight: 400, color: 'var(--muted)' }}>
                                            {t('documents.dataSecurityDescription')}
                                        </p>
                                    </div>

                                    <div className="deposit-actions">
                                        <h3 style={{ fontSize: 18, fontWeight: 500 }}>{t('documents.identityVerification')}</h3>
                                        <p className='mb-0' style={{ fontSize: 15, fontWeight: 400, color: 'var(--muted)' }}>
                                            {t('documents.identityVerificationDescription')}
                                        </p>

                                        {/* Progress Component */}
                                        <div className="verification-progress mt-4">
                                            <div className="progress-steps">
                                                <div className={`progress-step ${currentStep >= 1 && currentStep < 4 ? 'active' : ''} ${currentStep === 4 ? 'completed' : ''}`}>
                                                    <div className="step-icon">
                                                        {currentStep === 4 ? <IoCheckmarkCircleOutline size={20} /> : <IoCloudUploadOutline size={20} />}
                                                    </div>
                                                    <div className="step-content">
                                                        <h4 className="step-title">{t('documents.steps.uploadDocuments')}</h4>
                                                        <p className="step-description">{t('documents.steps.uploadDocumentsDescription')}</p>
                                                    </div>
                                                </div>


                                                <div className={`progress-step ${currentStep === 4 ? 'active' : ''}`}>
                                                    <div className="step-icon">
                                                        <IoPlayOutline size={20} />
                                                    </div>
                                                    <div className="step-content">
                                                        <h4 className="step-title">{t('documents.steps.startTrading')}</h4>
                                                        <p className="step-description">{t('documents.steps.startTradingDescription')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Step Progress Indicator */}
                                        <div className="step-progress-indicator mt-4">
                                            <div className="progress-header">
                                                <h4 className="progress-title">{t('documents.progress.title')}</h4>
                                                <div className="progress-counter">
                                                    {currentStep === 4 ? t('documents.progress.completed') : `${t('documents.progress.step')} ${currentStep} / ${steps.length}`}
                                                </div>
                                            </div>
                                            <div className="progress-bar-container">
                                                {/* <div className="progress-bar">
                                                    <div
                                                        className="progress-fill"
                                                        style={{ width: `${currentStep === 4 ? 100 : (completedSteps.length / steps.length) * 100}%` }}
                                                    ></div>
                                                </div> */}
                                                <div className="progress-steps-indicators my-4">
                                                    {steps.map((step, index) => (
                                                        <div
                                                            key={step.id}
                                                            className={`step-indicator ${completedSteps.includes(step.id) ? 'completed' :
                                                                currentStep === step.id ? 'active' : 'pending'
                                                                }`}
                                                        >
                                                            <div className="step-indicator-circle">
                                                                {completedSteps.includes(step.id) ? (
                                                                    <IoCheckmarkCircleOutline size={20} />
                                                                ) : currentStep === step.id ? (
                                                                    <IoCloudUploadOutline size={20} />
                                                                ) : (
                                                                    <IoDocumentTextOutline size={20} />
                                                                )}
                                                            </div>
                                                            <div className="step-indicator-label">{step.title}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Current Step Upload */}
                                        <div className="current-step-upload mt-5">
                                            {steps.map((step) => (
                                                <div
                                                    key={step.id}
                                                    className={`step-upload-card ${currentStep === step.id ? 'active' : 'hidden'}`}
                                                >
                                                    <div className="step-upload-header">
                                                        <div className="step-icon">{step.icon}</div>
                                                        <div className="step-content">
                                                            <h5 className="step-title">{step.title}</h5>
                                                            <p className="step-description">{step.description}</p>
                                                        </div>
                                                        {completedSteps.includes(step.id) && (
                                                            <div className="step-completed">
                                                                <IoCheckmarkCircleOutline size={24} className="text-success" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="step-upload-area">
                                                        <input
                                                            type="file"
                                                            id={step.documentType}
                                                            accept={step.documentType === 'addressProof' ? 'image/*,application/pdf' : 'image/*'}
                                                            onChange={(e) => handleFileChange(step.documentType, e.target.files[0])}
                                                            className="file-input"
                                                            disabled={completedSteps.includes(step.id)}
                                                        />
                                                        <label
                                                            htmlFor={step.documentType}
                                                            className={`file-upload-label ${completedSteps.includes(step.id) ? 'completed' : ''}`}
                                                        >
                                                            {completedSteps.includes(step.id) ? (
                                                                <>
                                                                    <IoCheckmarkCircleOutline size={32} />
                                                                    <span>{t('documents.upload.uploaded')}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <IoCloudUploadOutline size={32} />
                                                                    <span>{t('documents.upload.selectFile')}</span>
                                                                </>
                                                            )}
                                                        </label>

                                                        {documents[step.documentType] && fileInfo[step.documentType] && (
                                                            <div className="file-preview">
                                                                <div className="file-preview-header">
                                                                    <div className="file-preview-info">
                                                                        <div className="file-name">{fileInfo[step.documentType].name}</div>
                                                                        <div className="file-size">{formatFileSize(fileInfo[step.documentType].size)}</div>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => handleFileRemove(step.documentType, e)}
                                                                        className="remove-file-btn"
                                                                    >
                                                                        <IoTrashOutline size={16} />
                                                                    </button>
                                                                </div>
                                                                {filePreviews[step.documentType] && (
                                                                    <div className="file-preview-image">
                                                                        <img src={filePreviews[step.documentType]} alt="Preview" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Individual Upload Button - Moved outside step-upload-area */}
                                                    {!completedSteps.includes(step.id) && (
                                                        <div className="individual-upload-section mt-3">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleIndividualUpload(step.documentType);
                                                                }}
                                                                disabled={uploadingSteps[step.documentType] || !documents[step.documentType]}
                                                                className={`btn-upload-individual w-100 ${documents[step.documentType]
                                                                    ? 'btn-primary'
                                                                    : 'btn-secondary'
                                                                    }`}
                                                            >
                                                                {uploadingSteps[step.documentType] ? (
                                                                    <>
                                                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                            <span className="visually-hidden">{t('documents.upload.uploading')}</span>
                                                                        </div>
                                                                        {t('documents.upload.uploading')}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <IoCloudUploadOutline size={16} className="me-2" />
                                                                        {documents[step.documentType] ? t('documents.upload.uploadDocument') : t('documents.upload.selectFileFirst')}
                                                                    </>
                                                                )}
                                                            </button>
                                                            {!documents[step.documentType] && (
                                                                <p className="text-center mt-2 mb-2" style={{ fontSize: '14px' }}>
                                                                    {t('documents.upload.selectFileFirst')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Completion Message */}
                                        {currentStep === 4 && (
                                            <div className="upload-completion-section mt-4">
                                                <div className="completion-message">
                                                    <IoCheckmarkCircleOutline size={48} className="text-success mb-3" />
                                                    <h4 className="completion-title">{t('documents.completion.title')}</h4>
                                                    <p className="completion-description">
                                                        {t('documents.completion.description')}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Sağ Kolon - Bilgiler */}
                                {/* <div className="col-lg-4">
                                    <div className="deposit-actions">
                                        <h3 style={{ fontSize: 18, fontWeight: 500 }}>{t('documents.dataSecurity')}</h3>
                                        <p className='mb-0' style={{ fontSize: 15, fontWeight: 400, color: 'var(--muted)' }}>
                                            {t('documents.dataSecurityDescription')}
                                        </p>
                                    </div> */}

                                    {/* SSS */}
                                    {/* <div className="info-section mt-lg-4">
                                        <div className="faq-header">
                                            <h4 className="info-title">{t('documents.faq.title')}</h4>
                                        </div>
                                        <div className="faq-items">
                                            <div className="faq-item">
                                                <div className="faq-question">
                                                    <span>{t('documents.faq.withdrawalTime')}</span>
                                                    <IoPlayCircleOutline size={16} />
                                                </div>
                                            </div>
                                            <div className="faq-item">
                                                <div className="faq-question">
                                                    <span>{t('documents.faq.minAmount')}</span>
                                                    <IoPlayCircleOutline size={16} />
                                                </div>
                                            </div>
                                            <div className="faq-item">
                                                <div className="faq-question">
                                                    <span>{t('documents.faq.feeCalculation')}</span>
                                                    <IoPlayCircleOutline size={16} />
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                {/* </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'var(--card)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                    },
                    success: {
                        style: {
                            border: '1px solid #10b981',
                        },
                    },
                    error: {
                        style: {
                            border: '1px solid #ef4444',
                        },
                    },
                }}
            />
        </div>
    );
}
